/*
 * 결제내역 엑셀 → 이용자별 수납. 표는 브라우저에서만 읽고, 서버에는 연결된 줄의 이용자 id·날짜·금액·분류만 보낸다.
 * 은행 입출금 내역(거래일시·입금금액·거래기록사항), 바우처 결제 내역(결제일·결제금액·대상자명) 등 머리글이 달라도 알아본다.
 */

export interface PaymentColumns { date: number; amount: number; text: number[] }
export interface PaymentRow { line: number; paidOn: string; amount: number; text: string }

const DATE_HEAD = /거래\s*일|거래일시|결제\s*일|승인\s*일|지급\s*일|입금\s*일|일자|일시|날짜/;
const AMOUNT_HEAD = /입금|결제\s*금액|승인\s*금액|지급액|수납|금액/;
const OUT_HEAD = /출금|잔액|수수료|취소/;
const TEXT_HEAD = /기록사항|내용|적요|성명|이름|대상자|이용자|보낸\s*분|입금자|의뢰인|메모|비고/;

/** 머리글 줄과 열 위치를 찾는다(위 20줄 안에서 날짜·금액 머리글이 함께 있는 첫 줄). */
export function detectPaymentColumns(table: readonly (readonly string[])[]): { header: number; cols: PaymentColumns } | null {
  for (let r = 0; r < Math.min(table.length, 20); r++) {
    const row = table[r]!.map((v) => String(v ?? '').replace(/\s+/g, ' ').trim());
    const date = row.findIndex((h) => DATE_HEAD.test(h) && !/기간/.test(h));
    const amountIn = row.findIndex((h) => /입금/.test(h) && !/잔액/.test(h));
    const amount = amountIn >= 0 ? amountIn : row.findIndex((h) => AMOUNT_HEAD.test(h) && !OUT_HEAD.test(h));
    if (date < 0 || amount < 0) continue;
    const text = row.flatMap((h, i) => (i !== date && i !== amount && TEXT_HEAD.test(h) ? [i] : []));
    return { header: r, cols: { date, amount, text } };
  }
  return null;
}

/** 엑셀 날짜(일련번호)·2026.01.05·2026-01-05 10:22·20260105 → YYYY-MM-DD */
export function paymentDate(v: string): string | null {
  const s = String(v ?? '').trim();
  if (/^\d{5}(\.\d+)?$/.test(s)) {
    const d = new Date(Date.UTC(1899, 11, 30) + Math.floor(Number(s)) * 86400_000);
    return d.toISOString().slice(0, 10);
  }
  const m = /^(\d{4})[.\-/년\s]*(\d{1,2})[.\-/월\s]*(\d{1,2})/.exec(s);
  if (!m) return null;
  const iso = `${m[1]}-${m[2]!.padStart(2, '0')}-${m[3]!.padStart(2, '0')}`;
  const t = Date.parse(`${iso}T00:00:00Z`);
  return !Number.isNaN(t) && new Date(t).toISOString().slice(0, 10) === iso ? iso : null;
}

/** 1,234,000 · 1234000원 · ₩1,234 → 1234000. 숫자가 아니거나 0 이하면 null */
export function paymentAmount(v: string): number | null {
  const s = String(v ?? '').replace(/[,\s원₩]/g, '');
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
  const n = Math.round(Number(s));
  return n > 0 ? n : null;
}

/** 머리글 아래 줄들 중 날짜와 0원보다 큰 금액이 있는 줄만 */
export function readPaymentRows(table: readonly (readonly string[])[], header: number, cols: PaymentColumns): PaymentRow[] {
  const rows: PaymentRow[] = [];
  for (let r = header + 1; r < table.length; r++) {
    const row = table[r]!;
    const paidOn = paymentDate(row[cols.date] ?? '');
    const amount = paymentAmount(row[cols.amount] ?? '');
    if (!paidOn || !amount) continue;
    const text = cols.text.map((i) => String(row[i] ?? '').trim()).filter(Boolean).join(' · ');
    rows.push({ line: r + 1, paidOn, amount, text });
  }
  return rows;
}

/**
 * 줄의 글자(입금자·기록사항 등)에 이용자 이름이 들어 있으면 그 이용자로 연결한다.
 * 두 명 이상이 맞으면(긴 이름에 들어 있는 짧은 이름은 빼고도) 연결하지 않고 직접 고르게 한다.
 */
export function matchPaymentClient<T extends { id: string; displayName: string }>(text: string, clients: readonly T[]): { client?: T; candidates: T[] } {
  const flat = text.replace(/\s+/g, '');
  const hits = clients.filter((c) => c.displayName.length >= 2 && flat.includes(c.displayName.replace(/\s+/g, '')));
  const kept = hits.filter((c) => !hits.some((o) => o !== c && o.displayName.length > c.displayName.length && o.displayName.includes(c.displayName)));
  return kept.length === 1 ? { client: kept[0], candidates: kept } : { candidates: kept };
}

/** 이미 올린 결제와 같은 줄인지(같은 이용자·결제일·금액·분류). 같은 파일을 두 번 올려도 겹쳐 들어가지 않게 한다. */
export const paymentKey = (r: { clientId: string; paidOn: string; amount: number; program?: string }) => `${r.clientId}|${r.paidOn}|${r.amount}|${r.program ?? ''}`;
